let toastTimer;

const applyLocale = (root = document) => window.__HXY_I18N__?.applyLocale(root);
const translateText = (value) => window.__HXY_I18N__?.translateText(value) ?? value;

const appUrl = (target = '/') => {
  const basePath = window.__HXY_BASE_PATH__ || '';
  if (!target.startsWith('/') || !basePath || target === basePath || target.startsWith(`${basePath}/`)) return target;
  return `${basePath}${target}`;
};

const appAssetUrl = (source) => source?.startsWith('/assets/') ? appUrl(source) : source;

const SCROLL_STORAGE_PREFIX = 'hxy:scroll-position:';
if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';

const scrollStorageKey = () => `${SCROLL_STORAGE_PREFIX}${window.location.pathname}${window.location.search}`;
let pendingScrollTimers = [];
let scrollCorrectionGeneration = 0;

const cancelPendingScroll = () => {
  scrollCorrectionGeneration += 1;
  pendingScrollTimers.forEach((timer) => window.clearTimeout(timer));
  pendingScrollTimers = [];
};

const scheduleScrollCorrections = (apply) => {
  cancelPendingScroll();
  const generation = scrollCorrectionGeneration;
  const applyIfCurrent = () => {
    if (generation !== scrollCorrectionGeneration) return;
    apply();
  };
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      if (generation !== scrollCorrectionGeneration) return;
      applyIfCurrent();
      pendingScrollTimers = [120, 500, 1000].map((delay) => window.setTimeout(applyIfCurrent, delay));
    });
  });
};

const saveCurrentScroll = () => {
  try {
    window.sessionStorage.setItem(scrollStorageKey(), JSON.stringify({ x: window.scrollX, y: window.scrollY }));
  } catch { /* Browser-native restoration remains available. */ }
};

const restoreCurrentScroll = () => {
  let position;
  try { position = JSON.parse(window.sessionStorage.getItem(scrollStorageKey()) || 'null'); } catch { return false; }
  if (!Number.isFinite(position?.x) || !Number.isFinite(position?.y)) return false;
  const expectedUrl = window.location.href;
  const apply = () => {
    if (window.location.href !== expectedUrl) return;
    window.scrollTo({ left: position.x, top: position.y, behavior: 'auto' });
  };
  scheduleScrollCorrections(apply);
  return true;
};

const scrollToCurrentAnchor = () => {
  if (!window.location.hash) return false;
  const target = document.getElementById(decodeURIComponent(window.location.hash.slice(1)));
  if (!target) return false;
  const expectedUrl = window.location.href;
  const apply = () => {
    if (window.location.href !== expectedUrl || !target.isConnected) return;
    target.scrollIntoView({ block: 'start', behavior: 'auto' });
  };
  scheduleScrollCorrections(apply);
  return true;
};

document.addEventListener('pointerdown', cancelPendingScroll, { capture: true, passive: true });
window.addEventListener('wheel', cancelPendingScroll, { capture: true, passive: true });
window.addEventListener('touchstart', cancelPendingScroll, { capture: true, passive: true });
document.addEventListener('keydown', cancelPendingScroll, { capture: true });

let scrollStorageFrame = 0;
window.addEventListener('scroll', () => {
  if (scrollStorageFrame) return;
  scrollStorageFrame = window.requestAnimationFrame(() => {
    scrollStorageFrame = 0;
    saveCurrentScroll();
  });
}, { passive: true });
window.addEventListener('pagehide', saveCurrentScroll);
window.addEventListener('pageshow', (event) => {
  const navigationType = window.performance.getEntriesByType('navigation')[0]?.type;
  if ((event.persisted || navigationType === 'back_forward') && restoreCurrentScroll()) return;
  scrollToCurrentAnchor();
});

const stripBasePath = (pathname) => {
  const basePath = window.__HXY_BASE_PATH__ || '';
  const normalized = pathname.replace(/\/+$/, '') || '/';
  if (!basePath) return normalized;
  if (normalized === basePath) return '/';
  return normalized.startsWith(`${basePath}/`) ? normalized.slice(basePath.length) : normalized;
};

const updateGlobalNavigation = () => {
  const path = stripBasePath(window.location.pathname);
  const isCurrentPath = (linkPath) => {
    const collectionAreaMatch = linkPath === '/collections/all' && (path === '/collections' || path.startsWith('/collections/') || path.startsWith('/products/'));
    const detailFamilyMatch = ['/pages/estate', '/pages/channel'].includes(linkPath) && path.startsWith(`${linkPath}-`);
    return path === linkPath || collectionAreaMatch || detailFamilyMatch || (linkPath !== '/' && path.startsWith(`${linkPath}/`));
  };
  document.querySelectorAll('.primary-nav a').forEach((link) => {
    const linkPath = stripBasePath(new URL(link.href, window.location.origin).pathname);
    const current = isCurrentPath(linkPath);
    link.classList.toggle('is-current', current);
    if (current) link.setAttribute('aria-current', 'page');
    else link.removeAttribute('aria-current');
  });
  document.querySelectorAll('.primary-nav__dropdown').forEach((dropdown) => {
    const parent = [...dropdown.children].find((child) => child.matches('a'));
    const childIsCurrent = [...dropdown.querySelectorAll('.primary-nav__submenu a')].some((link) => link.classList.contains('is-current'));
    if (!parent || !childIsCurrent) return;
    parent.classList.add('is-current');
    parent.removeAttribute('aria-current');
  });
};

const readCart = () => {
  try { return JSON.parse(localStorage.getItem('hxy-cart') || '[]'); } catch { return []; }
};

const writeCart = (items) => localStorage.setItem('hxy-cart', JSON.stringify(items));

const showToast = (message) => {
  const toast = document.querySelector('[data-toast]');
  if (!toast) return;
  toast.textContent = translateText(message);
  toast.classList.add('is-visible');
  window.clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => toast.classList.remove('is-visible'), 2200);
};

const updateCartCount = () => {
  const count = readCart().reduce((total, item) => total + item.quantity, 0);
  document.querySelectorAll('.cart-count').forEach((node) => { node.textContent = String(count); });
};

const addToCart = (button) => {
  const quantity = Number(button.closest('.product-detail')?.querySelector('[data-quantity]')?.textContent || 1);
  const product = {
    id: button.dataset.productId || 'estate-selection',
    name: button.dataset.productName || '庄园精选茶品',
    price: Number(button.dataset.productPrice || 398),
    image: button.closest('.product-card, .product-detail')?.querySelector('img')?.getAttribute('src') || appUrl('/assets/tea-black.jpg'),
  };
  const items = readCart();
  const current = items.find((item) => item.id === product.id);
  if (current) current.quantity += quantity;
  else items.push({ ...product, quantity });
  writeCart(items);
  updateCartCount();
  showToast('商品已加入购物袋');
};

const renderCartItems = () => {
  const shell = document.querySelector('[data-cart-shell]');
  if (!shell) return;
  const items = readCart();
  const itemContainer = shell.querySelector('[data-cart-items]');
  const totalNode = shell.querySelector('[data-cart-total]');
  const checkoutLink = shell.querySelector('[data-checkout-link]');
  if (!items.length) {
    itemContainer.innerHTML = `<div class="cart-empty"><span class="cart-empty__mark">袋</span><h2>购物袋还是空的</h2><p>从一杯庄园好茶开始，慢慢挑选你的茶席。</p><a class="button button--navy" href="${appUrl('/collections/all')}">去选茶</a></div>`;
    totalNode.textContent = '¥ 0';
    if (checkoutLink) { checkoutLink.href = appUrl('/collections/all'); checkoutLink.textContent = '先去选茶'; }
    applyLocale(shell);
    return;
  }
  if (checkoutLink) { checkoutLink.href = appUrl('/checkout'); checkoutLink.textContent = '前往结算'; }
  itemContainer.innerHTML = items.map((item) => `<article class="cart-item"><a href="${appUrl(`/products/${item.id}`)}"><img src="${appAssetUrl(item.image)}" alt="${item.name}"></a><div><p class="eyebrow eyebrow--gold">HXY TEA ESTATE</p><h3><a href="${appUrl(`/products/${item.id}`)}">${item.name}</a></h3><p>¥ ${item.price}</p><div class="cart-item__actions"><button type="button" data-cart-minus data-product-id="${item.id}" aria-label="减少${item.name}数量">−</button><span>${item.quantity}</span><button type="button" data-cart-plus data-product-id="${item.id}" aria-label="增加${item.name}数量">+</button><button class="cart-item__remove" type="button" data-cart-remove data-product-id="${item.id}">移除</button></div></div></article>`).join('');
  totalNode.textContent = `¥ ${items.reduce((total, item) => total + item.price * item.quantity, 0)}`;
  applyLocale(shell);
};

const renderCheckoutItems = () => {
  const summary = document.querySelector('[data-checkout-summary]');
  if (!summary) return;
  const items = readCart();
  const itemContainer = summary.querySelector('[data-checkout-items]');
  const totalNode = summary.querySelector('[data-checkout-total]');
  const form = document.querySelector('.checkout-form');
  if (!items.length) {
    itemContainer.innerHTML = `<div class="checkout-empty"><p>购物袋中还没有商品。</p><a class="text-link" href="${appUrl('/collections/all')}">先去选茶 <span>→</span></a></div>`;
    totalNode.textContent = '¥ 0';
    form?.querySelectorAll('input, button').forEach((control) => { control.disabled = true; });
    applyLocale(summary);
    return;
  }
  itemContainer.innerHTML = items.map((item) => `<article class="checkout-item"><img src="${appAssetUrl(item.image)}" alt="${item.name}"><div><a href="${appUrl(`/products/${item.id}`)}">${item.name}</a><span>数量 ${item.quantity}</span></div><strong>¥ ${item.price * item.quantity}</strong></article>`).join('');
  totalNode.textContent = `¥ ${items.reduce((total, item) => total + item.price * item.quantity, 0)}`;
  applyLocale(summary);
};

const siteBackToTopButton = document.querySelector('[data-site-back-to-top]');
const updateSiteBackToTopButton = () => {
  if (!siteBackToTopButton) return;
  const visible = !document.body.classList.contains('is-home-page') && window.scrollY > 120;
  siteBackToTopButton.classList.toggle('is-visible', visible);
  siteBackToTopButton.setAttribute('aria-hidden', String(!visible));
  siteBackToTopButton.tabIndex = visible ? 0 : -1;
};
window.addEventListener('scroll', updateSiteBackToTopButton, { passive: true });

document.addEventListener('click', (event) => {
  const clickedSiteBackToTop = event.target.closest('[data-site-back-to-top]');
  if (clickedSiteBackToTop) {
    event.preventDefault();
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: 0, left: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
    const main = document.querySelector('main');
    if (main) {
      if (!main.hasAttribute('tabindex')) main.setAttribute('tabindex', '-1');
      main.focus({ preventScroll: true });
    }
    return;
  }
  const menuButton = event.target.closest('[data-menu-toggle]');
  if (menuButton) {
    const isOpen = document.body.classList.toggle('menu-open');
    menuButton.setAttribute('aria-label', isOpen ? '关闭菜单' : '打开菜单');
    menuButton.setAttribute('aria-expanded', String(isOpen));
    return;
  }
  if (event.target.closest('.primary-nav a')) {
    document.body.classList.remove('menu-open');
    document.querySelector('[data-menu-toggle]')?.setAttribute('aria-expanded', 'false');
  }
  const addButton = event.target.closest('[data-add-to-cart]');
  if (addButton) { event.preventDefault(); addToCart(addButton); return; }
  // Shopify product forms submit to the native cart; do not intercept them with the Vite demo cart.
  if (event.target.closest('form[action*="/cart/add"]')) return;
  const thumbButton = event.target.closest('[data-product-thumb]');
  if (thumbButton) {
    const mainImage = document.querySelector('[data-product-main-image]');
    const thumbImage = thumbButton.querySelector('img');
    if (mainImage && thumbImage) { mainImage.src = thumbImage.src; mainImage.alt = thumbImage.alt; }
    document.querySelectorAll('[data-product-thumb]').forEach((button) => button.classList.toggle('is-active', button === thumbButton));
    return;
  }
  const optionButton = event.target.closest('[data-product-option]');
  if (optionButton) {
    document.querySelectorAll('[data-product-option]').forEach((button) => button.classList.toggle('is-selected', button === optionButton));
    return;
  }
  const items = readCart();
  const productId = event.target.closest('[data-product-id]')?.dataset.productId;
  if (event.target.closest('[data-cart-remove]')) {
    writeCart(items.filter((item) => item.id !== productId)); renderCartItems(); updateCartCount(); return;
  }
  if (event.target.closest('[data-cart-plus]') || event.target.closest('[data-cart-minus]')) {
    const item = items.find((entry) => entry.id === productId);
    if (item) item.quantity = Math.max(0, item.quantity + (event.target.closest('[data-cart-plus]') ? 1 : -1));
    writeCart(items.filter((item) => item.quantity > 0)); renderCartItems(); updateCartCount();
  }
  if (event.target.closest('[data-quantity-plus]') || event.target.closest('[data-quantity-minus]')) {
    const control = event.target.closest('.quantity');
    const quantityNode = control?.querySelector('input[name="quantity"], [data-quantity]');
    if (!quantityNode) return;
    const isInput = quantityNode instanceof HTMLInputElement;
    const current = Number(isInput ? quantityNode.value : quantityNode.textContent);
    const minimum = isInput ? Number(quantityNode.min || 1) : 1;
    const maximum = isInput && quantityNode.max ? Number(quantityNode.max) : Infinity;
    const quantity = (Number.isFinite(current) ? current : minimum) + (event.target.closest('[data-quantity-plus]') ? 1 : -1);
    const value = String(Math.min(maximum, Math.max(minimum, Math.floor(quantity))));
    if (isInput) {
      quantityNode.value = value;
      quantityNode.dispatchEvent(new Event('change', { bubbles: true }));
    } else quantityNode.textContent = value;
  }
});

document.addEventListener('submit', (event) => {
  if (event.target.matches('.newsletter__form')) { event.preventDefault(); event.target.reset(); showToast('订阅成功，感谢关注华祥苑'); }
  if (event.target.matches('.contact-form')) { event.preventDefault(); event.target.reset(); showToast('留言已收到，我们会尽快联系你'); }
  if (event.target.matches('.account-form')) { event.preventDefault(); showToast('演示环境已记录登录请求'); }
  if (event.target.matches('.checkout-form')) { event.preventDefault(); writeCart([]); window.__HXY_NAVIGATE__?.(appUrl('/checkout/thank-you')); }
  if (event.target.matches('.search-page__form')) { return; }
});

document.addEventListener('change', (event) => {
  if (event.target.matches('[data-collection-sort]')) event.target.form?.requestSubmit();
});

document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape') { document.body.classList.remove('menu-open'); document.querySelector('[data-menu-toggle]')?.setAttribute('aria-expanded', 'false'); }
});

const setupHomeExperience = () => {
  const experience = document.querySelector('[data-home-experience]');
  if (!experience || experience.dataset.ready === 'true') return;
  experience.dataset.ready = 'true';
  const screens = [...experience.querySelectorAll('[data-home-screen]')];
  const links = [...experience.querySelectorAll('[data-home-target]')];
  const backToTopButton = experience.querySelector('[data-home-back-to-top]');
  let activeIndex = 0;
  let locked = false;
  let touchStartY = null;
  let touchStartX = null;
  let lastActiveIndex = -1;
  const animateChannelStats = (screen) => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    screen.querySelectorAll('.channel-stats strong').forEach((counter) => {
      const target = Number(counter.dataset.homeCountTarget || counter.textContent.match(/\d+/)?.[0] || 0);
      counter.dataset.homeCountTarget = String(target);
      const textNode = [...counter.childNodes].find((node) => node.nodeType === Node.TEXT_NODE);
      const start = performance.now();
      const render = (now) => {
        const progress = Math.min((now - start) / 760, 1);
        const value = Math.round(target * (1 - Math.pow(1 - progress, 3)));
        if (textNode) textNode.nodeValue = String(value); else counter.textContent = String(value);
        if (progress < 1) window.requestAnimationFrame(render);
      };
      window.requestAnimationFrame(render);
    });
  };
  const updateBackToTopButton = () => {
    if (!backToTopButton) return;
    const visible = activeIndex > 0;
    backToTopButton.classList.toggle('is-visible', visible);
    backToTopButton.setAttribute('aria-hidden', String(!visible));
    backToTopButton.tabIndex = visible ? 0 : -1;
  };
  const setActive = (index) => {
    activeIndex = Math.max(0, Math.min(index, screens.length - 1));
    updateBackToTopButton();
    screens.forEach((screen, screenIndex) => {
      screen.classList.toggle('is-active', screenIndex === activeIndex);
      screen.classList.toggle('is-before', screenIndex < activeIndex);
      screen.classList.toggle('is-after', screenIndex > activeIndex);
    });
    links.forEach((link) => {
      const current = link.dataset.homeTarget === screens[activeIndex]?.dataset.homeScreen;
      link.classList.toggle('is-active', current);
      if (current) link.setAttribute('aria-current', 'page'); else link.removeAttribute('aria-current');
    });
    if (activeIndex !== lastActiveIndex && screens[activeIndex]?.dataset.homeScreen === 'tea-space') animateChannelStats(screens[activeIndex]);
    lastActiveIndex = activeIndex;
  };
  const goTo = (index) => {
    const next = Math.max(0, Math.min(index, screens.length - 1));
    if (!screens[next] || next === activeIndex && window.scrollY >= screens[next].offsetTop - 20) return;
    setActive(next);
    screens[next].scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  links.forEach((link) => link.addEventListener('click', (event) => {
    if (!link.getAttribute('href')?.startsWith('#')) return;
    const target = screens.findIndex((screen) => screen.dataset.homeScreen === link.dataset.homeTarget);
    if (target < 0) return;
    event.preventDefault();
    goTo(target);
  }));
  backToTopButton?.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    goTo(0);
  });
  const observer = new IntersectionObserver((entries) => {
    const visible = entries.filter((entry) => entry.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
    if (visible) setActive(screens.indexOf(visible.target));
  }, { threshold: [0.55, 0.75] });
  screens.forEach((screen) => observer.observe(screen));
  experience.addEventListener('wheel', (event) => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || window.innerWidth < 768 || Math.abs(event.deltaY) < 12) return;
    event.preventDefault();
    if (locked) return;
    locked = true;
    goTo(activeIndex + (event.deltaY > 0 ? 1 : -1));
    window.setTimeout(() => { locked = false; }, 760);
  }, { passive: false });
  experience.addEventListener('touchstart', (event) => {
    if (window.innerWidth >= 768 || event.touches.length !== 1) return;
    touchStartY = event.changedTouches[0]?.clientY ?? null;
    touchStartX = event.changedTouches[0]?.clientX ?? null;
  }, { passive: true });
  experience.addEventListener('touchmove', (event) => {
    if (window.innerWidth >= 768 || touchStartY === null || touchStartX === null) return;
    const touch = event.changedTouches[0];
    const deltaY = touchStartY - (touch?.clientY ?? touchStartY);
    const deltaX = touchStartX - (touch?.clientX ?? touchStartX);
    if (Math.abs(deltaY) > 12 && Math.abs(deltaY) > Math.abs(deltaX)) event.preventDefault();
  }, { passive: false });
  experience.addEventListener('touchend', (event) => {
    if (touchStartY === null || touchStartX === null || window.innerWidth >= 768) return;
    const delta = touchStartY - (event.changedTouches[0]?.clientY ?? touchStartY);
    const horizontalDelta = touchStartX - (event.changedTouches[0]?.clientX ?? touchStartX);
    touchStartY = null;
    touchStartX = null;
    if (locked || Math.abs(delta) <= 45 || Math.abs(delta) <= Math.abs(horizontalDelta)) return;
    locked = true;
    goTo(activeIndex + (delta > 0 ? 1 : -1));
    window.setTimeout(() => { locked = false; }, 760);
  }, { passive: true });
  experience.addEventListener('touchcancel', () => { touchStartY = null; touchStartX = null; }, { passive: true });
  document.addEventListener('keydown', (event) => {
    if (!document.querySelector('[data-home-experience]') || ['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement?.tagName)) return;
    if (event.key === 'ArrowDown' || event.key === 'PageDown') { event.preventDefault(); goTo(activeIndex + 1); }
    if (event.key === 'ArrowUp' || event.key === 'PageUp') { event.preventDefault(); goTo(activeIndex - 1); }
  });
  setActive(0);
};

let brandProductSwiperInstances = [];

const setupBrandProductCarousels = () => {
  brandProductSwiperInstances = brandProductSwiperInstances.filter((instance) => {
    if (instance.el?.isConnected) return true;
    instance.destroy(true, true);
    return false;
  });
  const SwiperConstructor = window.Swiper;
  if (!SwiperConstructor) return;
  document.querySelectorAll('[data-brand-product-carousel]').forEach((carousel) => {
    if (carousel.dataset.ready === 'true') return;
    const swiperElement = carousel.querySelector('[data-brand-product-swiper]');
    if (!swiperElement) return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const instance = new SwiperConstructor(swiperElement, {
      effect: 'coverflow',
      centeredSlides: true,
      slidesPerView: 'auto',
      spaceBetween: 20,
      rewind: true,
      speed: reduceMotion ? 0 : 850,
      grabCursor: true,
      slideToClickedSlide: true,
      watchSlidesProgress: true,
      autoplay: reduceMotion ? false : {
        delay: 4200,
        disableOnInteraction: false,
        pauseOnMouseEnter: true,
      },
      keyboard: {
        enabled: true,
        onlyInViewport: true,
      },
      navigation: {
        prevEl: carousel.querySelector('[data-brand-product-prev]'),
        nextEl: carousel.querySelector('[data-brand-product-next]'),
      },
      pagination: {
        el: carousel.querySelector('[data-brand-product-pagination]'),
        clickable: true,
      },
      coverflowEffect: {
        rotate: 0,
        stretch: '22%',
        depth: 220,
        scale: 0.78,
        modifier: 1,
        slideShadows: false,
      },
      a11y: {
        prevSlideMessage: '上一张',
        nextSlideMessage: '下一张',
        paginationBulletMessage: '前往第 {{index}} 张',
      },
    });
    carousel.dataset.ready = 'true';
    brandProductSwiperInstances.push(instance);
  });
};

updateCartCount();
renderCartItems();
renderCheckoutItems();
updateGlobalNavigation();
updateSiteBackToTopButton();
setupHomeExperience();
setupBrandProductCarousels();
applyLocale();

window.addEventListener('hxy:localechange', () => {
  renderCartItems();
  renderCheckoutItems();
  applyLocale();
});

window.addEventListener('hxy:routechange', () => {
  updateCartCount();
  renderCartItems();
  renderCheckoutItems();
  updateSiteBackToTopButton();
  setupHomeExperience();
  setupBrandProductCarousels();
  applyLocale();
});

window.addEventListener('load', setupBrandProductCarousels, { once: true });
